/**
 * StoresList Component
 *
 * This component displays a list of American Golf stores, grouped by
 * the first letter of their names. Each store is presented as a
 * clickable link that navigates to a specified URL (currently empty).
 *
 * Features:
 * - Stores are organized alphabetically by their first letter,
 *   providing a clear and structured view.
 * - Utilizes a responsive grid layout to display store names.
 * - Integrates Next.js's Link component for client-side navigation.
 *
 * Data Structure:
 * - The stores are defined in an array of objects, each containing:
 *   - name: The name of the store.
 *   - url: The link to the store's page (currently placeholders).

 */

import React from 'react';
import Link from 'next/link';
import Typography from 'components/commercetools-ui/atoms/typography';

interface Store {
  name: string;
  url: string;
}

const StoresList = ({ storeData }: { storeData: Store[] }) => {
  // const stores = [
  //   { name: 'Aberdeen', url: '' },
  //   { name: 'Aintree', url: '' },
  //   { name: 'Basingstoke', url: '' },
  //   { name: 'Borehamwood', url: '' },
  //   { name: 'Broome Manor', url: '' },
  //   { name: 'Belfast', url: '' },
  //   { name: 'Bournemouth', url: '' },
  //   { name: 'Brighton', url: '' },
  //   { name: 'Camberley', url: '' },
  //   { name: 'Chester', url: '' },
  //   { name: 'Coventry', url: '' },
  //   { name: 'Derby', url: '' },
  //   { name: 'Edinburgh (Braid Hills)', url: '' },
  //   { name: 'Forth View', url: '' },
  //   { name: 'Gateshead', url: '' },
  //   { name: 'Hedge End', url: '' },
  //   { name: 'Inverness', url: '' },
  //   { name: 'Kettering', url: '' },
  //   { name: 'Leeds (Crown Point)', url: '' },
  //   { name: 'Liverpool Golf Centre', url: '' },
  //   { name: 'Milton Keynes', url: '' },
  //   { name: 'Newcastle', url: '' },
  //   { name: 'Oxford', url: '' },
  //   { name: 'Peterborough', url: '' },
  //   { name: 'Riverside', url: '' },
  //   { name: 'Salisbury', url: '' },
  //   { name: 'Stevenage', url: '' },
  //   { name: 'Telford', url: '' },
  //   { name: 'Wakefield', url: '' },
  //   { name: 'York', url: '' },
  // ];

  const storeDataSorted = storeData;
  const sortedStores = storeDataSorted?.sort((a: any, b: any) => {
    const nameA = a?.name?.toLowerCase() || '';
    const nameB = b?.name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });

  const groupedStores: Record<string, Store[]> = sortedStores?.reduce((acc: any, store: any) => {
    const firstLetter = store?.name?.[0]?.toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [store];
    } else {
      acc[firstLetter]?.push(store);
    }
    return acc;
  }, {} as Record<string, Store[]>);

  return (
    <div className="p-15 lg:ml-4">
      <Typography className="text-20  font-bold uppercase">American Golf Stores</Typography>
      {Object.entries(groupedStores).map(([letter, stores]) => (
        <div key={letter} className="py-20" id={`store_section_${letter}`}>
          <h3 className="mb-6 text-20 font-bold">{letter}</h3>
          <ul className="grid grid-cols-1 items-end gap-3 md:grid-cols-3 lg:grid-cols-4">
            {stores.map((store) => (
              <li className="mb-2 break-words text-16 " key={`${store?.name}_key`}>
                <Link href={''}>{store?.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default StoresList;
